import ssmlGenerator from './ssml-generator';
import ssmlHelper from 'view/insertion/ssml-helper';
import { bufferToWave } from 'app-core/audio';

export default {
	computed: {
		insertionContextMenuItems() {
			if (this.creative?.IsNewsMLTemplate) {
				return [
					{	
						text: this.$tCaption('News ML'),
						template: 'service',
						__type: 'customFields',
						imageUrl: `${window.origin}/${require('assets/newsml.png')}`,
						items: 
						[
							...['Headline', 'Content'].map(f => ({
								text: f,
								template: 'output',
								icon: 'f',
								insertValue: `[${f.toLowerCase()}@newsml]`,
								allowFieldValue: true
							}))
						]
					}
				]
			}
			return null;
		}
	},
	data() {
		return {
			
		}
	},
	methods: {
		async onAudioInsertionOverdubbing(result, track) {
			this.isLoading++;
			try {
				let aggTrackName = `${track.__aggregatedTrack.name} ${this.$tCaption('(overdub)')}`;
				let aggTrack = track.__tracker.aggregatedTracks.find(f => f.name === aggTrackName);
				if (!aggTrack) {
					aggTrack = this.createNewAggregatedTrack({ insertAfter: track.__aggregatedTrack });
					aggTrack.name = `${track.__aggregatedTrack.name} ${this.$tCaption('(overdub)')}`;
				}

				const endOffset = track.insertion.EndOffset || 0;

				if (__DEBUG__) {
					console.debug('onAudioInsertionOverdubbing', { result, track, endOffset, td: track.trimmedDuration });
				}
				
				track.endOffset += track.trimmedDuration - result?.start;

				let insertion = {
					Oid: null,
					Type: 'SsmlInsertion',
					StartTime: parseInt((track?.start + result?.start) * 1000),
					Ssml: result.ssml,
					Language: result?.language,
					Voice: result?.voice,
					Rate: 1,
					Pitch: 1,
					JsonConfig: {
						trackNumber: aggTrack?.trackNumber
					},
					CreativeId: this.creative?.Oid
				}

				let preparedSsml = ssmlHelper.prepareSsml(insertion.Ssml, this.$Data.formatObjectToLowerCase(insertion));
				if (preparedSsml.hasDefaultVoiceReplaced) {
					insertion.JsonConfig.hasDefaultVoiceReplaced = true;
					insertion.JsonConfig.defaultVoiceName = preparedSsml?.voice;
				}

				const { buffer, base64 } = await ssmlGenerator.generateSSMLAudio(this.$refs.tracker, insertion, true);
				insertion.Duration = parseInt(buffer?.duration * 1000);
				insertion = (await this.$Data.createUpdatePureInsertion(insertion))?.Data;

				await track.__duplicate(track, true, {
					StartTime: insertion.Duration + insertion.StartTime,
					StartOffset: (track.insertion.StartOffset || 0) + parseInt(result?.start * 1000) + parseInt(result?.duration * 1000),
					EndOffset: endOffset
				});

				await this.$azure.uploadInsertionAudio(insertion, base64)
					.then(() => {
						let track = this.getTrackFromInsertion(insertion);
						track.src = base64;
						track.__isSrcLoaded = false;
						track.__isSubTrack = !!this.editingParentInsertion;

						this.processAddTrack(track);
						this.$refs.tracker.setBase64ForTrack(track, base64);
						this.calcVisibleTracks();

						return track;
					})
					.catch((ex) => {
						this.$notifyError(ex);
					});
			} finally {
				this.isLoading--;
			}
		},
		async onAudioInsertionConversion(result, track) {
			this.isLoading++;
			try {
				let aggTrackName = `${track.__aggregatedTrack.name} ${this.$tCaption('(conversion)')}`;
				let aggTrack = track.__tracker.aggregatedTracks.find(f => f.name === aggTrackName);
				if (!aggTrack) {
					aggTrack = this.createNewAggregatedTrack({ insertAfter: track.__aggregatedTrack });
					aggTrack.name = aggTrackName;
				}
				track.__aggregatedTrack.muted = true;

				let insertion = {
					Oid: null,
					Type: 'SsmlInsertion',
					StartTime: parseInt(result?.start * 1000) || 0,
					Language: result?.language,
					Voice: result?.voice,
					Rate: 1,
					Pitch: 1,
					Name: (result?.speech || '').substring(0, 255),
					JsonConfig: {
						trackNumber: aggTrack?.trackNumber
					},
					CreativeId: this.creative?.Oid,
					Ssml: result.ssml,
				}

				let preparedSsml = ssmlHelper.prepareSsml(insertion.Ssml, this.$Data.formatObjectToLowerCase(insertion));
				if (preparedSsml.hasDefaultVoiceReplaced) {
					insertion.JsonConfig.hasDefaultVoiceReplaced = true;
					insertion.JsonConfig.defaultVoiceName = preparedSsml?.voice;
				}

				const { buffer, base64 } = await ssmlGenerator.generateSSMLAudio(this.$refs.tracker, insertion, true);
				insertion.Duration = parseInt(buffer?.duration * 1000);
				insertion = (await this.$Data.createUpdatePureInsertion(insertion))?.Data;

				await this.$azure.uploadInsertionAudio(insertion, base64)
					.then(() => {
						let track = this.getTrackFromInsertion(insertion);
						track.src = base64;
						track.__isSrcLoaded = false;
						track.__isSubTrack = !!this.editingParentInsertion;

						this.processAddTrack(track);
						this.$refs.tracker.setBase64ForTrack(track, base64);
						this.calcVisibleTracks();

						return track;
					})
					.catch((ex) => {
						this.$notifyError(ex);
					});
			} finally {
				this.isLoading--;
			}
		},
		async onAudioInsertionTextBasedAudioEditor(track, audio) {
			if (__DEBUG__)
				console.debug('onAudioInsertionTextBasedAudioEditor', { track, audio });

			const dataUri = await this.$Data.blob2Base64(audio);
			track.__setBase64(dataUri);
			track.__isLoading++;
			this.isLoading++;
			await this.$azure.uploadInsertionAudio(track.insertion, dataUri)
				.then(() => {
					this.calcVisibleTracks();
					return track;
				})				
				.catch((ex) => {
					this.$notifyError(ex);
				})
				.finally(() => {
					track.__isLoading--;
					this.isLoading--;
				});
		},
		freezeSsml(track) {
			this.$confirm(void 0, this.$tCaption('This action will convert the current track into an audio track. This process is irreversible. Do you want to proceed?')).then(async () => {
				let blob = await bufferToWave(track.__buffer);

				track.__isLoading++;
				try {
					await this.createAudioInsertionFromAzureFile({
						fileName: track.name().substr(0, 30) || this.$tCaption('New audio'),
						blob,
						insertionInfo: {
							JsonConfig: {
								trackNumber: track.insertion.JsonConfig.trackNumber
							},
							StartTime: track.insertion.StartTime
						}
					});

					await this.removeTrack(track, true);

					this.$notifySuccess(this.$tCaption('Track has been successfully freezed'));
				} finally {
					track.__isLoading--;
				}
			});
		}
	},
};
